/**
 * Contract-request status → business pipeline stage mapping.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *   WHY THIS FILE EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * The KR Agency Hub UI presents contracting activity in a 5-stage business
 * pipeline (New / Under Review / Missing Information / Approved / Other) plus
 * a separate appointment-driven "Activated" tile. The backend, however,
 * emits ~18 different raw workflow statuses across historical rows —
 * including several near-duplicate spellings for the same business concept
 * (e.g. `Missing_Info` / `Missing_Information` / `Missing_Certification`).
 *
 * This module is the ONE PLACE where raw statuses are grouped into stages.
 * Every contracting surface (Agent Command Center, Agency Command Center,
 * Requests Page, Kanban Board) reads through the helpers here. Consumers
 * must NEVER switch on a raw status literal directly — the moment the
 * backend adds a new alias, hard-coded UI checks silently break in four
 * places simultaneously. Read through `statusToStage`, `statusesForStage`,
 * `statusToBadgeVariant`, `stageToCanonicalStatus`, or `countRequestsByStage`
 * instead. Unit-test coverage lives in `__tests__/statusMapping.test.js`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *   BACKEND SOURCE OF TRUTH
 * ═══════════════════════════════════════════════════════════════════════════
 * The backend defines the canonical set of contract workflow statuses in
 * `/app/backend/server.py`:
 *
 *   CONTRACT_STATUS = Literal[
 *     "Submitted", "Requested", "In_Progress",
 *     "Pending_Carrier", "Carrier_Pending",
 *     "Missing_Information", "Missing_Info", "Missing_Certification",
 *     "Submitted_To_Carrier", "Invitation_Sent_To_Agent", "Waiting_On_Agent",
 *     "Pending_Release", "Release_Pending",
 *     "Approved", "Active", "Declined",
 *     "On_Hold", "Terminated", "Archived"
 *   ]
 *
 * The backend also defines `WORKFLOW_STATUS_ALIASES` which normalizes legacy
 * spellings into a smaller canonical set. However, the raw `current_status`
 * value returned to the frontend from `GET /api/contract-requests` is NOT
 * always pre-normalized — historical rows still leak `Carrier_Pending`,
 * `Missing_Information`, `Waiting_On_Agent`, `Release_Pending`, etc.
 *
 * This file is the SINGLE SOURCE OF TRUTH on the frontend for translating any
 * raw workflow status into a business pipeline stage. Anywhere the UI needs
 * to bucket a request into New / Under Review / Missing Information /
 * Approved / Other, it MUST import from this module. Do NOT inline stage
 * status arrays in components.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *   BUSINESS STAGE DEFINITIONS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   NEW ─────────────── Requested, Submitted, Pending
 *     Rationale: freshly-created request that hasn't started moving through
 *     the workflow yet. `Submitted` and `Pending` are legacy aliases that the
 *     backend normalizes to `Requested`, but old rows may still carry them.
 *
 *   UNDER_REVIEW ────── Pending_Carrier, Carrier_Pending, Submitted_To_Carrier,
 *                       Invitation_Sent_To_Agent, Waiting_On_Agent,
 *                       In_Progress, Pending_Release, Release_Pending
 *     Rationale: request is actively moving through the workflow — either
 *     with the carrier, with the agent, or in a release-preparation state.
 *     `Pending_Carrier` (canonical) and `Carrier_Pending` (legacy) are the
 *     most common historical mismatch and were the primary driver of the
 *     Phase 18.3 "Other" bucket blowout.
 *
 *   MISSING_INFO ────── Missing_Info, Missing_Information,
 *                       Missing_Certification, On_Hold
 *     Rationale: request is blocked pending an input from the agent, the
 *     carrier, or an internal reviewer. `On_Hold` semantically belongs here
 *     even though the backend keeps it in ACTIVE_PIPELINE_STATUSES —
 *     operators triage On_Hold the same way as Missing_Info.
 *
 *   APPROVED ────────── Approved, Active
 *     Rationale: successful terminal state (carrier approved the request).
 *     `Active` is a rarely-emitted alias that some legacy carrier imports
 *     used before the terminology was standardized.
 *
 *   OTHER (catch-all) ── Declined, Terminated, Archived + any unknown future
 *     Rationale: known-terminal-but-not-successful states plus a safety net
 *     for any future status the backend may introduce that hasn't been mapped
 *     yet. This guarantees the reconciliation invariant:
 *
 *         New + Under Review + Missing Information + Approved + Other
 *           === total contract requests
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *   ACTIVATED
 * ═══════════════════════════════════════════════════════════════════════════
 * "Activated" is NOT a request-level status. It is computed against the
 * `carrier_appointments` collection where `appointment_status === 'Active'`
 * and a `writing_number` has been issued. See `countActiveAppointments()`
 * below. Callers must pass appointments — not requests — to that helper.
 */

// Master mapping: business stage id → raw status literals it accepts.
export const PIPELINE_STAGE_MAP = Object.freeze({
  new: [
    'Requested',
    'Submitted',
    'Pending',
  ],
  review: [
    'Pending_Carrier',
    'Carrier_Pending',
    'Submitted_To_Carrier',
    'Invitation_Sent_To_Agent',
    'Waiting_On_Agent',
    'In_Progress',
    'Pending_Release',
    'Release_Pending',
  ],
  missing: [
    'Missing_Info',
    'Missing_Information',
    'Missing_Certification',
    'On_Hold',
  ],
  approved: [
    'Approved',
    'Active',
  ],
});

// Ordered list of ALL business stage ids (including the implicit 'other'
// catch-all which is NOT a key of PIPELINE_STAGE_MAP). Consumers that need
// to iterate every stage — for grid rendering, deep-link builders, tests —
// should iterate this array instead of `Object.keys(PIPELINE_STAGE_MAP)`.
export const PIPELINE_STAGE_IDS = Object.freeze([
  'new', 'review', 'missing', 'approved', 'other',
]);

// Human-readable label for each stage. Kept in one place so agent + agency
// views (and any future consumer) speak the same language.
export const PIPELINE_STAGE_LABELS = Object.freeze({
  new: 'New',
  review: 'Under Review',
  missing: 'Missing Information',
  approved: 'Approved',
  other: 'Other',
});

// Every status literal that is explicitly mapped. Anything NOT in this set
// falls into the "Other" catch-all — including the known-terminal states
// `Declined`, `Terminated`, `Archived` plus any unknown future status.
const MAPPED_STATUSES = Object.freeze(
  Object.values(PIPELINE_STAGE_MAP).flat()
);

/**
 * Resolve a raw workflow status → business stage id.
 * @param {string|null|undefined} status
 * @returns {'new'|'review'|'missing'|'approved'|'other'}
 */
export function statusToStage(status) {
  if (!status) return 'other';
  for (const [stage, statuses] of Object.entries(PIPELINE_STAGE_MAP)) {
    if (statuses.includes(status)) return stage;
  }
  return 'other';
}

/**
 * Count contract requests bucketed by business stage. Returns an object with
 * keys `new`, `review`, `missing`, `approved`, `other` and a numeric value
 * for each. Reconciliation invariant guaranteed:
 *   Object.values(countRequestsByStage(reqs)).reduce((a,b)=>a+b) === reqs.length
 *
 * @param {Array<{current_status: string}>} requests
 */
export function countRequestsByStage(requests = []) {
  const counts = { new: 0, review: 0, missing: 0, approved: 0, other: 0 };
  for (const r of requests) counts[statusToStage(r.current_status)] += 1;
  return counts;
}

/**
 * Activated is APPOINTMENT-driven, not request-driven. A carrier appointment
 * is "activated" when its status is Active AND a writing number has been
 * issued by the carrier. See AgencyContractingPipeline for usage.
 *
 * @param {Array<{appointment_status: string, writing_number?: string}>} appointments
 */
export function countActiveAppointments(appointments = []) {
  return appointments.filter(
    (a) => a.appointment_status === 'Active' && a.writing_number
  ).length;
}

// Exported for advanced callers (e.g. deep-link URL builders for the Other
// tile). Prefer `statusToStage` and `countRequestsByStage` in components.
export const ALL_MAPPED_STATUSES = MAPPED_STATUSES;

/**
 * Map a raw workflow status → the StatusBadge variant that best represents it
 * in the UI. Callers should use this instead of ad-hoc `.includes('Missing')`
 * checks so status colouring stays consistent with the pipeline bucketing.
 *
 *   approved → 'success'
 *   missing  → 'warning'
 *   other    → 'neutral'
 *   new / review → 'info'
 */
export function statusToBadgeVariant(status) {
  switch (statusToStage(status)) {
    case 'approved': return 'success';
    case 'missing':  return 'warning';
    case 'other':    return 'neutral';
    case 'new':
    case 'review':
    default:         return 'info';
  }
}

/**
 * Canonical raw workflow status for a given business stage id.
 *
 * When the UI needs to translate a business-stage selection back into an
 * actionable backend status (e.g. the Kanban move dialog, or a status_in
 * filter list on the Requests page) it uses this helper. Each stage picks
 * the SAFEST canonical status:
 *
 *   new      → 'Requested'          (first citizen of the New bucket)
 *   review   → 'Pending_Carrier'    (canonical review state)
 *   missing  → 'Missing_Info'       (canonical short form)
 *   approved → 'Approved'
 *   other    → 'Declined'           (safest terminal — reversible-in-audit)
 *
 * Callers that need the full list of statuses that fall INTO a stage should
 * use `PIPELINE_STAGE_MAP[stageId]` instead (or `statusesForStage`).
 */
const CANONICAL_STATUS_BY_STAGE = Object.freeze({
  new:      'Requested',
  review:   'Pending_Carrier',
  missing:  'Missing_Info',
  approved: 'Approved',
  other:    'Declined',
});

export function stageToCanonicalStatus(stageId) {
  return CANONICAL_STATUS_BY_STAGE[stageId] || null;
}

/**
 * Return the list of raw workflow statuses that fall INTO a given business
 * stage. For the 4 mapped stages this is just `PIPELINE_STAGE_MAP[stageId]`.
 * For 'other' — a negative bucket by definition — this returns the enumerated
 * known-terminal statuses (Declined, Terminated, Archived) so callers that
 * need a positive `status_in` filter list don't have to redefine it.
 */
const OTHER_KNOWN_STATUSES = Object.freeze(['Declined', 'Terminated', 'Closed', 'Archived']);

export function statusesForStage(stageId) {
  if (stageId === 'other') return OTHER_KNOWN_STATUSES;
  return PIPELINE_STAGE_MAP[stageId] || [];
}
